import {
  reportRecipientsApi,
  type ReportClient,
  type ClientReportRecipients,
  type HeadquartersContact,
  type ReportCadence,
} from './api';
import { navigation } from './navigation.svelte';
import { ApiFailure } from './api-failure';
import { ContactSearch } from './contact-search.svelte';

/**
 * A lista de envios de um cliente, como a tela a edita: o cliente escolhido
 * fica na URL (?cliente=), a busca procura contato do HQ e cada inclusão ou
 * retirada recarrega a lista da API.
 */
export class ReportRecipientsEditor {
  clients = $state<ReportClient[]>([]);
  recipients = $state<ClientReportRecipients | null>(null);
  cadence = $state<ReportCadence>('daily');
  error = $state<string | null>(null);
  busy = $state(false);

  readonly tenantId = $derived(navigation.valueOf('cliente') ?? this.clients[0]?.tenantId ?? null);
  readonly client = $derived(this.clients.find((candidate) => candidate.tenantId === this.tenantId) ?? null);
  /**
   * O mesmo contato pode receber o diário e o semanal: o que já existe é o par
   * número + cadência — o número, porque o HQ tem o mesmo em mais de um customer.
   */
  private readonly listed = $derived(
    new Set(
      this.recipients?.whatsapp.flatMap((row) =>
        row.subscriptions.map((subscription) => this.listingKey(row.contact, subscription.cadence)),
      ) ?? [],
    ),
  );

  readonly search = new ContactSearch((err) => (this.error = this.messageOf(err)));

  constructor(private readonly onUnauthorized: () => void) {}

  isListed(contact: HeadquartersContact): boolean {
    return this.listed.has(this.listingKey(contact, this.cadence));
  }

  /** Liga a tela ao cliente da URL e à busca; chamar na inicialização do componente. */
  watch(): void {
    $effect(() => {
      const tenantId = this.tenantId;
      if (!tenantId) return;
      this.recipients = null;
      void this.loadRecipients(tenantId);
    });

    this.search.watch();
  }

  async loadClients(): Promise<void> {
    try {
      this.clients = await reportRecipientsApi.clients();
    } catch (err) {
      this.error = this.messageOf(err);
    }
  }

  async add(contact: HeadquartersContact): Promise<void> {
    await this.change((tenantId) => reportRecipientsApi.add(tenantId, contact.id, this.cadence));
  }

  async remove(recipientId: number): Promise<void> {
    await this.change((tenantId) => reportRecipientsApi.remove(tenantId, recipientId));
  }

  private async change(write: (tenantId: string) => Promise<unknown>): Promise<void> {
    const tenantId = this.tenantId;
    if (!tenantId) return;
    this.busy = true;
    try {
      await write(tenantId);
      await this.loadRecipients(tenantId);
    } catch (err) {
      this.error = this.messageOf(err);
    } finally {
      this.busy = false;
    }
  }

  private async loadRecipients(tenantId: string): Promise<void> {
    try {
      const recipients = await reportRecipientsApi.of(tenantId);
      // Resposta de um cliente que já saiu da tela não sobrescreve a do cliente atual.
      if (tenantId !== this.tenantId) return;
      this.recipients = recipients;
      this.error = null;
    } catch (err) {
      this.error = this.messageOf(err);
    }
  }

  private listingKey(contact: HeadquartersContact, cadence: ReportCadence): string {
    return `${contact.identification ?? contact.id}:${cadence}`;
  }

  private messageOf(err: unknown): string {
    return ApiFailure.messageOf(err, this.onUnauthorized);
  }
}
